import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Epg, Layout } from 'planby';
import { Form, Row, Col } from 'react-bootstrap';
import { lastDayOfMonth, format, getDaysInMonth, lastDayOfYear, addDays } from 'date-fns';

import { usePlanner } from './usePlanner';
import PlannerModals from './components/planner-modals';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import {
  Timeline,
  ChannelItem,
  ProgramItem,
  Line,
  CustomTimeline,
  LiveTime,
  Program,
  DroppableArea,
  DraggableBoxes,
} from './components';
import './plan-view.css'; // Add this import for custom styles
import { fetchPlanner, fetchByWorkTicketId } from './store/slice';
import { useDispatch, useSelector } from 'react-redux';
import { selectPlannerData, isPlannerLoading } from './store/selectors';
import { fetchLocations } from '../../core/redux/location/slice';
import Filter from './components/filter';

const PlanView = () => {
  const plannerRef = useRef(null);
  const {
    isLoading,
    getEpgProps,
    getLayoutProps,
    onChangeDate,
    calendarMode,
    dates,
    removeElement,
  } = usePlanner(plannerRef);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const isToday = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const startStr = format(new Date(dates.start), 'yyyy-MM-dd');
    const endStr = format(new Date(dates.end), 'yyyy-MM-dd');
    return today >= startStr && today <= endStr;
  }, [dates.start, dates.end]);
  const [selectedMonth, setSelectedMonth] = useState(
    (new Date().getMonth() + 1).toString().padStart(2, '0'),
  );
  const [selectedDay, setSelectedDay] = useState('');
  const [start, setStart] = useState(`${selectedYear}-${selectedMonth}-01`);
  const [end, setEnd] = useState(
    `${selectedYear}-${(new Date().getMonth() + 2).toString().padStart(2, '0')}-01`,
  );
  const [status, setStatus] = useState('All');
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchPlanner());
    dispatch(
      fetchLocations({
        name: '',
        description: '',
      }),
    );
  }, []);

  const generateYearsList = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear + 2;
    const endYear = currentYear - 5;
    return Array.from({ length: startYear - endYear + 1 }, (_, index) => startYear - index);
  }, []);

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => {
      const month = index + 1;
      const date = new Date(2000, index, 1); // Using 2000 as dummy year
      return {
        value: month.toString().padStart(2, '0'),
        label: format(date, 'MMMM'),
      };
    });
  }, []);

  const days = useMemo(() => {
    if (!selectedYear || !selectedMonth) return [];
    const daysInMonth = getDaysInMonth(new Date(selectedYear, parseInt(selectedMonth) - 1));
    return Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1;
      return {
        value: day.toString().padStart(2, '0'),
        label: day.toString().padStart(2, '0'),
      };
    });
  }, [selectedYear, selectedMonth]);

  const handleYearChange = event => {
    const year = event.target.value;
    setSelectedYear(year);
    setSelectedMonth(''); // Reset month
    setSelectedDay(''); // Reset day
    const startDate = `${year}-01-01`;
    handleTimelineClick(startDate, 'year');
  };

  const handleMonthUnselect = () => {
    setSelectedMonth('');
    setSelectedDay('');
    const startDate = `${selectedYear}-01-01`;
    handleTimelineClick(startDate, 'year');
  };

  const handleMonthChange = event => {
    const month = event.target.value;
    if (month === '') {
      handleMonthUnselect();
      return;
    }
    setSelectedMonth(month);
    setSelectedDay('');
    const startDate = `${selectedYear}-${month}-01`;
    handleTimelineClick(startDate, 'month');
  };

  const handleDayUnselect = () => {
    setSelectedDay('');
    const startDate = `${selectedYear}-${selectedMonth}-01`;
    handleTimelineClick(startDate, 'month');
  };

  const handleDayChange = event => {
    const day = event.target.value;
    if (day === '') {
      handleDayUnselect();
      return;
    }
    setSelectedDay(day);
    const startDate = `${selectedYear}-${selectedMonth}-${day}`;
    handleTimelineClick(startDate, 'week');
  };

  const handleTimelineClick = (date, type) => {
    let startDate, endDate;
    if (type === 'month') {
      startDate = `${date}T00:00:00`;
      endDate = format(
        new Date(new Date(startDate).setMonth(new Date(startDate).getMonth() + 1)),
        "yyyy-MM-dd'T'24:00:00",
      );
      setSelectedMonth(format(new Date(date), 'MM'));
      setSelectedDay('');
      onChangeDate(startDate, endDate, 'week');
      handleStartEndDateChange(startDate, endDate);
    } else if (type === 'week') {
      startDate = `${date}T00:00:00`;
      endDate = `${date}T24:00:00`;
      const day = format(new Date(date), 'dd');
      setSelectedDay(day);
      onChangeDate(startDate, endDate, 'day');
      handleStartEndDateChange(startDate, endDate);
    } else if (type === 'year') {
      startDate = `${date}T00:00:00`;
      endDate = format(lastDayOfYear(new Date(startDate)), "yyyy-MM-dd'T'24:00:00");
      onChangeDate(startDate, endDate, 'month');
      handleStartEndDateChange(startDate, endDate);
    }
  };

  const handleStartEndDateChange = (start, end) => {
    setStart(start);
    setEnd(end);
  };

  const handleSearch = (fromDate, toDate, currentStatus) => {
    let startDate, endDate;
    setStatus(currentStatus);
    startDate = `${fromDate}T00:00:00`;
    endDate = format(addDays(new Date(toDate), 1), "yyyy-MM-dd'T'00:00:00");
    onChangeDate(startDate, endDate, 'week');
  };

  const handleProgramEdit = id => {
    dispatch(fetchByWorkTicketId(id));
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header transfer">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Production planning</h4>
              </div>
            </div>
          </div>

          <div className="d-flex gap-3">
            <div className="position-relative">
              <select
                className="form-select form-select-lg"
                aria-label=".form-select-lg example"
                value={selectedYear}
                onChange={handleYearChange}
              >
                <option value="">Select year</option>
                {generateYearsList.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="position-relative">
              <select
                className="form-select form-select-lg"
                value={selectedMonth}
                onChange={handleMonthChange}
              >
                <option value="">Select month</option>
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              {selectedMonth && (
                <button className="btn-clear-select" onClick={handleMonthUnselect} type="button">
                  ×
                </button>
              )}
            </div>

            <div className="position-relative">
              <select
                className="form-select form-select-lg"
                value={selectedDay}
                onChange={handleDayChange}
                disabled={!selectedMonth}
              >
                <option value="">Select day</option>
                {days.map(day => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
              {selectedDay && (
                <button className="btn-clear-select" onClick={handleDayUnselect} type="button">
                  ×
                </button>
              )}
            </div>
          </div>

          <DndProvider backend={HTML5Backend}>
            <div ref={plannerRef} className={selectedDay ? 'day-selected' : ''}>
              <DraggableBoxes />
              <Filter onHandleSearch={handleSearch} start={start} end={end} />
              <div style={{ height: '68vh', width: '100%' }}>
                <DroppableArea removeElement={removeElement}>
                  <Epg isLoading={isLoading} {...getEpgProps()}>
                    <Layout
                      {...getLayoutProps()}
                      renderLine={props => <Line {...props} />}
                      renderCurrentTime={props => <LiveTime {...props} />}
                      renderTimeline={props => (
                        <CustomTimeline
                          isCurrentTime={true}
                          handleTimelineClick={handleTimelineClick}
                          mode={{ type: calendarMode, style: 'modern' }}
                          startDate={dates.start}
                          endDate={dates.end}
                          isToday={isToday}
                          {...props}
                        />
                      )}
                      renderProgram={({ program, ...rest }) => (
                        <Program
                          onDoubleClick={() => handleProgramEdit(program.data.id)}
                          key={program.data.id}
                          program={program}
                          startDate={dates.start}
                          endDate={dates.end}
                          // isVisible={status == "All" || program.data.status == status}
                          isVisible={status === 'All' || program.data.status === status}
                          {...rest}
                        />
                      )}
                      renderChannel={({ channel, ...rest }) => (
                        <ChannelItem key={channel.uuid} channel={channel} {...rest} />
                      )}
                    />
                  </Epg>
                </DroppableArea>
              </div>
            </div>
          </DndProvider>
        </div>
      </div>
      <PlannerModals />
    </div>
  );
};

export default PlanView;
